import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useEducationProgress } from "../hooks/useEducationProgress";
import { CurriculumService } from "../services/curriculumService";
import { CourseCatalog } from "./education/CourseCatalog";
import { ProgressDashboard } from "./education/ProgressDashboard";
import { LessonPlayer } from "./education/LessonPlayer";
import {
  AchievementGrid,
  StreakDisplay,
  PointsDisplay,
} from "./education/GamificationComponents";
import { Leaderboard } from "./education/Leaderboard";
import { EducationalErrorBoundary } from "./education/EducationalErrorBoundary";
import { useToolState } from "../contexts/ToolStateContext";
import { mockLessons } from "../data/lessons/mockLessons";
import { Lesson } from "../types/education";
import type {
  SkillMetrics,
  LearningInsight,
} from "../services/skillAssessmentService";
import { GamificationService } from "../services/gamificationService";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  BookOpen,
  Trophy,
  TrendingUp,
  Cloud,
  CloudOff,
  Download,
  Upload,
  RefreshCw,
  BarChart3,
  Target,
  ChevronRight,
  Clock,
  History,
} from "lucide-react";
import { useAlert } from '@ascend/alert';

interface EducationHubProps {
  enableCloudSync?: boolean;
  enableSkillAssessment?: boolean;
}

interface BreadcrumbItem {
  label: string;
  value: string;
}

interface RecentlyViewedLesson {
  lesson: Lesson;
  viewedAt: Date;
  progress: number;
}

export const EducationHub: React.FC<EducationHubProps> = ({
  enableCloudSync = false,
  enableSkillAssessment = true,
}) => {
  const curriculumService = useMemo(
    () => new CurriculumService(mockLessons),
    [],
  );
  const { getToolState, setToolState } = useToolState();
  const [activeTab, setActiveTab] = useState(
    getToolState("education")?.activeTab ?? "catalog",
  );
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedLesson[]>(
    [],
  );

  const {
    educationData,
    isLoading,
    analytics,
    isSyncing,
    syncError,
    markLessonCompleted,
    addQuizScore,
    addBookmark,
    removeBookmark,
    canAccessLesson,
    getPrerequisiteStatus,
    syncWithCloud,
    createBackup,
    restoreBackup,
    resetProgress,
  } = useEducationProgress({
    enableCloudSync,
    enableSkillAssessment,
    curriculumService,
  });

  const [backupId, setBackupId] = useState<string | null>(null);
  const [importId, setImportId] = useState("");
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);

  // Get all available topics from curriculum
  const allTopics = curriculumService.getAllTopics();
  const allLearningPaths = curriculumService.getAllLearningPaths();

  // Generate breadcrumb navigation
  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const crumbs: BreadcrumbItem[] = [
      { label: "Education Hub", value: "catalog" },
    ];

    if (currentLesson) {
      crumbs.push({ label: "Lesson", value: "lesson" });
      crumbs.push({
        label: currentLesson.title,
        value: `lesson-${currentLesson.id}`,
      });
    } else if (activeTab !== "catalog") {
      const tabLabels: Record<string, string> = {
        overview: "Overview",
        progress: "Progress",
        analytics: "Analytics",
        settings: "Settings",
      };
      crumbs.push({
        label: tabLabels[activeTab] || activeTab,
        value: activeTab,
      });
    }

    return crumbs;
  }, [activeTab, currentLesson]);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback(
    (value: string) => {
      if (value === "catalog") {
        setCurrentLesson(null);
        setActiveTab("catalog");
      } else if (value === "lesson" && currentLesson) {
        setActiveTab("lesson");
      } else {
        setActiveTab(value);
        setCurrentLesson(null);
      }
    },
    [currentLesson],
  );

  // Add lesson to recently viewed
  const addToRecentlyViewed = useCallback(
    (lesson: Lesson) => {
      setRecentlyViewed((prev) => {
        const filtered = prev.filter((item) => item.lesson.id !== lesson.id);
        const newEntry: RecentlyViewedLesson = {
          lesson,
          viewedAt: new Date(),
          progress: educationData.progress[lesson.id]?.progress || 0,
        };
        return [newEntry, ...filtered].slice(0, 5); // Keep only 5 most recent
      });
    },
    [educationData.progress],
  );

  // Update recently viewed when current lesson changes
  useEffect(() => {
    if (currentLesson) {
      addToRecentlyViewed(currentLesson);
    }
  }, [currentLesson, addToRecentlyViewed]);

  useEffect(() => {
    setToolState("education", { activeTab });
  }, [activeTab, setToolState]);

  // Calculate completion percentage
  const completionPercentage =
    educationData.stats.totalLessons > 0
      ? Math.round(
          (educationData.stats.completedLessons /
            educationData.stats.totalLessons) *
            100,
        )
      : 0;

  // Handle backup creation
  const handleCreateBackup = async () => {
    try {
      setIsCreatingBackup(true);
      const id = await createBackup();
      setBackupId(id);
    } catch (error) {
      console.error("Failed to create backup:", error);
    } finally {
      setIsCreatingBackup(false);
    }
  };

  // Handle backup restore
  const handleRestoreBackup = async () => {
    if (!importId) return;

    try {
      setIsRestoringBackup(true);
      await restoreBackup(importId);
      setImportId("");
    } catch (error) {
      console.error("Failed to restore backup:", error);
    } finally {
      setIsRestoringBackup(false);
    }
  };

  const { alert: showAlert } = useAlert();

  // Handle lesson selection with prerequisite validation
  const handleLessonSelect = useCallback(
    async (lessonId: string) => {
      const lesson = mockLessons.find((l) => l.id === lessonId);
      if (!lesson) return;

      // Check if user can access this lesson based on prerequisites
      const prerequisites = lesson.prerequisites || [];
      const canAccess = canAccessLesson(lessonId, prerequisites);

      if (!canAccess) {
        // Get missing prerequisite information
        const prereqStatus = getPrerequisiteStatus(lessonId, prerequisites);
        const missingPrereqTitles = prereqStatus.missingPrereqs.map(
          (prereqId) => {
            const prereqLesson = mockLessons.find((l) => l.id === prereqId);
            return prereqLesson?.title || prereqId;
          },
        );

        // Show clear message about missing prerequisites
        await showAlert({
          title: 'Prerequisites required',
          message: `Complete these lessons first:\n\n${missingPrereqTitles.join("\n")}\n\nThese lessons provide the foundation needed for "${lesson.title}".`,
          type: 'warning',
        });
        return;
      }

      // Allow lesson access if prerequisites are met
      setCurrentLesson(lesson);
      setActiveTab("lesson");
      addToRecentlyViewed(lesson);
    },
    [addToRecentlyViewed, canAccessLesson, getPrerequisiteStatus, showAlert],
  );

  // Handle lesson completion
  const handleLessonComplete = useCallback(
    async (lessonId: string) => {
      await markLessonCompleted(lessonId);
      setCurrentLesson(null);
      setActiveTab("catalog");
    },
    [markLessonCompleted],
  );

  // Handle lesson progress update
  const handleLessonProgress = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_lessonId: string, _progress: number) => {
      // Progress is automatically tracked by the LessonPlayer component
      // This callback can be used for additional progress tracking if needed
    },
    [],
  );

  // Handle back to catalog
  const handleBackToCatalog = useCallback(() => {
    setCurrentLesson(null);
    setActiveTab("catalog");
  }, []);

  // Handle sync
  const handleSync = async () => {
    try {
      await syncWithCloud();
    } catch (error) {
      console.error("Sync failed:", error);
    }
  };

  const gamificationService = useMemo(() => new GamificationService(), []);
  const gamificationData = useMemo(() => gamificationService.getGamificationData(), [gamificationService]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading education data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Education Hub</h2>
          <p className="text-muted-foreground">
            Track your learning progress and achievements
          </p>
        </div>

        {/* Sync Status */}
        {enableCloudSync && (
          <div className="flex items-center space-x-2">
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Cloud className="h-4 w-4" />
            )}
            <span className="text-sm">
              {isSyncing ? "Syncing..." : "Synced"}
            </span>
            {syncError && (
              <Badge variant="destructive" className="text-xs">
                {syncError}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={isSyncing}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Sync
            </Button>
          </div>
        )}
      </div>

      {/* Breadcrumb Navigation */}
      <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.value}>
            {index > 0 && <ChevronRight className="h-4 w-4" />}
            <button
              onClick={() => handleBreadcrumbClick(crumb.value)}
              className={`hover:text-foreground transition-colors ${
                index === breadcrumbs.length - 1
                  ? "text-foreground font-medium"
                  : "hover:underline"
              }`}
              disabled={index === breadcrumbs.length - 1}
            >
              {crumb.label}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Narrow viewport: dropdown */}
        <div className="sm:hidden mb-4">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="catalog">Catalog</option>
            <option value="lesson" disabled={!currentLesson}>Lesson</option>
            <option value="overview">Overview</option>
            <option value="progress">Progress</option>
            <option value="gamification">🏆 Rewards</option>
            <option value="analytics">Analytics</option>
            <option value="settings">Settings</option>
          </select>
        </div>
        <TabsList className="hidden sm:grid w-full grid-cols-7">
          <TabsTrigger
            value="catalog"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            Catalog
          </TabsTrigger>
          <TabsTrigger
            value="lesson"
            currentValue={activeTab}
            onValueChange={setActiveTab}
            disabled={!currentLesson}
            className={!currentLesson ? "opacity-50" : ""}
          >
            Lesson
          </TabsTrigger>
          <TabsTrigger
            value="overview"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="progress"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            Progress
          </TabsTrigger>
          <TabsTrigger
            value="gamification"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            🏆 Rewards
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            currentValue={activeTab}
            onValueChange={setActiveTab}
          >
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Catalog Tab */}
        <TabsContent value="catalog" currentValue={activeTab}>
          <div className="space-y-6">
            <CourseCatalog
              curriculumService={curriculumService}
              lessons={mockLessons}
              completedLessons={Object.keys(educationData.progress).filter(
                (lessonId) => educationData.progress[lessonId]?.completed,
              )}
              bookmarkedLessons={educationData.bookmarks}
              onLessonSelect={handleLessonSelect}
              onBookmarkToggle={async (lessonId) => {
                // Handle bookmark toggle
                if (educationData.bookmarks.includes(lessonId)) {
                  await removeBookmark(lessonId);
                } else {
                  await addBookmark(lessonId);
                }
              }}
            />

            {/* Recently Viewed Lessons */}
            {recentlyViewed.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <History className="h-5 w-5 mr-2" />
                    Recently Viewed Lessons
                  </CardTitle>
                  <CardDescription>
                    Quickly access lessons you've been working on
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {recentlyViewed.map((item, index) => (
                      <div
                        key={`recent-${item.lesson.id}-${index}`}
                        className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleLessonSelect(item.lesson.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-sm line-clamp-2">
                            {item.lesson.title}
                          </h4>
                          <Badge variant="outline" className="text-xs ml-2">
                            {item.lesson.difficulty}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                          {item.lesson.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-muted-foreground">
                            <Clock className="h-3 w-3 mr-1" />
                            {item.viewedAt.toLocaleDateString()}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Progress
                              value={item.progress}
                              className="w-16 h-2"
                            />
                            <span className="text-xs text-muted-foreground">
                              {item.progress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Lesson Tab */}
        <TabsContent value="lesson" currentValue={activeTab}>
          {currentLesson ? (
            <LessonPlayer
              lesson={currentLesson}
              onBack={handleBackToCatalog}
              onComplete={handleLessonComplete}
              onProgressUpdate={handleLessonProgress}
              initialProgress={educationData.progress[currentLesson.id]}
            />
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">No lesson selected</p>
            </div>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" currentValue={activeTab}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Lessons
                </CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {educationData.stats.totalLessons}
                </div>
                <p className="text-xs text-muted-foreground">
                  Available in curriculum
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completed</CardTitle>
                <Trophy className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {educationData.stats.completedLessons}
                </div>
                <p className="text-xs text-muted-foreground">
                  {completionPercentage}% completion rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Time Spent
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {Math.round(educationData.stats.totalTimeSpent / 60)}h
                </div>
                <p className="text-xs text-muted-foreground">
                  {educationData.stats.totalTimeSpent} minutes
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Avg Quiz Score
                </CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {educationData.stats.averageQuizScore}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Across all quizzes
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Progress Overview */}
          <Card>
            <CardHeader>
              <CardTitle>Overall Progress</CardTitle>
              <CardDescription>
                Your learning journey at a glance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Completion Progress</span>
                    <span>{completionPercentage}%</span>
                  </div>
                  <Progress value={completionPercentage} className="mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress" currentValue={activeTab}>
          <ProgressDashboard
            curriculumService={curriculumService}
            educationData={educationData}
            analytics={analytics}
          />
        </TabsContent>

        {/* Gamification Tab */}
        <TabsContent value="gamification" currentValue={activeTab}>
          <div className="space-y-6">
            {!gamificationData ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">
                      Gamification Initializing
                    </h3>
                    <p className="text-muted-foreground">
                      Setting up your achievement system...
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  <PointsDisplay
                    totalPoints={gamificationData.totalPoints}
                    level={gamificationData.level}
                    experience={gamificationData.experience}
                  />
                  <StreakDisplay
                    currentStreak={gamificationData.currentStreak.count}
                    longestStreak={gamificationData.longestStreak.count}
                    lastActivityDate={
                      gamificationData.currentStreak.lastActivityDate
                    }
                  />
                  <Leaderboard className="md:col-span-1" />
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Trophy className="h-5 w-5 mr-2" />
                      Achievements
                    </CardTitle>
                    <CardDescription>
                      Track your learning milestones and unlock new
                      achievements
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AchievementGrid
                      achievements={gamificationData.achievements}
                      onAchievementClick={(achievement) => {
                        console.log("Achievement clicked:", achievement);
                      }}
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" currentValue={activeTab}>
          <EducationalErrorBoundary
            fallback={
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">Analytics Temporarily Unavailable</h3>
                    <p className="text-muted-foreground">
                      Complete more lessons to generate analytics data.
                    </p>
                  </div>
                </CardContent>
              </Card>
            }
          >
          {enableSkillAssessment && analytics ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Skills Overview
                  </CardTitle>
                  <CardDescription>
                    Your performance across different skills
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.skills.slice(0, 5).map((skill: SkillMetrics) => (
                      <div
                        key={skill.skillId}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-medium">
                          {skill.skillId}
                        </span>
                        <div className="flex items-center space-x-2">
                          <Progress value={skill.progress} className="w-20" />
                          <span className="text-sm text-muted-foreground">
                            {skill.progress}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Learning Insights</CardTitle>
                  <CardDescription>
                    Personalized insights based on your progress
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.insights
                      .slice(0, 3)
                      .map((insight: LearningInsight, index: number) => (
                        <div key={index} className="text-sm">
                          <p className="font-medium">{insight.title}</p>
                          <p className="text-muted-foreground">
                            {insight.description}
                          </p>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Recommended Next Steps</CardTitle>
                  <CardDescription>
                    Personalized recommendations for your learning journey
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-3">
                    {analytics.recommendations.nextTopics
                      .slice(0, 3)
                      .map((topic: string, index: number) => (
                        <div key={index} className="p-3 border rounded-lg">
                          <h4 className="font-medium text-sm">{topic}</h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Recommended based on your progress
                          </p>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">Analytics Disabled</h3>
                  <p className="text-muted-foreground">
                    Enable skill assessment to see detailed analytics and
                    insights.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          </EducationalErrorBoundary>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" currentValue={activeTab}>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Backup & Restore */}
            <Card>
              <CardHeader>
                <CardTitle>Backup & Restore</CardTitle>
                <CardDescription>
                  Manage your education data backups
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Button
                    onClick={handleCreateBackup}
                    disabled={isCreatingBackup}
                    className="w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {isCreatingBackup ? "Creating..." : "Create Backup"}
                  </Button>
                  {backupId && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Backup created: {backupId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium">Restore Backup</label>
                  <div className="flex space-x-2 mt-1">
                    <input
                      type="text"
                      value={importId}
                      onChange={(e) => setImportId(e.target.value)}
                      placeholder="Enter backup ID"
                      className="flex-1 px-3 py-2 border rounded-md text-sm"
                    />
                    <Button
                      onClick={handleRestoreBackup}
                      disabled={isRestoringBackup || !importId}
                      variant="outline"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {isRestoringBackup ? "Restoring..." : "Restore"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Features Status */}
            <Card>
              <CardHeader>
                <CardTitle>Features Status</CardTitle>
                <CardDescription>Current feature availability</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Cloud Sync</span>
                    {enableCloudSync ? (
                      <Badge variant="secondary">
                        <Cloud className="h-3 w-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <CloudOff className="h-3 w-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      Skill Assessment
                    </span>
                    {enableSkillAssessment ? (
                      <Badge variant="secondary">
                        <Target className="h-3 w-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <Target className="h-3 w-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Total Topics</span>
                    <Badge variant="outline">{allTopics.length}</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Learning Paths</span>
                    <Badge variant="outline">{allLearningPaths.length}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
